import { objectId } from '@feathr/hooks';

import type { IDonationCreatedEvent, IRawDonationSSEEvent } from './fundraisingEvents.types';

/** Key used by backend to store payment method in cust_params */
const PAYMENT_METHOD_KEY = 'Donation: Payment Method';

/**
 * Maps backend payment method strings to frontend enum values.
 * Backend values come from classify_payment_method() in fundraising.py.
 */
function mapPaymentMethod(backendValue: string | undefined): 'credit_card' | 'ach' {
  if (!backendValue) {
    return 'credit_card';
  }

  const normalized = backendValue.toLowerCase();
  if (normalized.includes('ach') || normalized.includes('bank')) {
    return 'ach';
  }

  // Credit Card, Apple Pay, Google Pay, and Other all map to credit_card
  return 'credit_card';
}

/**
 * Splits a full name into first and last name.
 * Handles edge cases like single names, multiple spaces, etc.
 *
 * @param fullName - The full name to split
 * @returns Object with firstName and lastName
 */
function splitName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  if (!trimmed) {
    return { firstName: 'Anonymous', lastName: 'Donor' };
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');
  return { firstName, lastName };
}

/**
 * Transforms a raw SSE donation event from the backend into the internal FE format.
 *
 * @param raw - The raw SSE event from the backend
 * @returns Transformed event in IDonationCreatedEvent format
 *
 * @example
 * ```ts
 * const raw: IRawDonationSSEEvent = {
 *   donor_name: "Jane Doe",
 *   donation_amount: "100.00",
 *   donation_type: "One-time",
 *   donation_time: "2026-04-21T15:30:00+00:00",
 *   growth_credits: 10,
 *   form_name: "Spring Fundraiser"
 * };
 * const event = transformRawDonationEvent(raw);
 * // event.donor_first_name === "Jane"
 * // event.amount === 100
 * // event.donation_type === "one_time"
 * ```
 */
function transformRawDonationEvent(raw: IRawDonationSSEEvent): IDonationCreatedEvent {
  const { firstName, lastName } = splitName(raw.donor_name);
  const isTestEvent = raw.cust_params?._test === '1';

  // Extract payment method and transaction ID from cust_params if available
  const paymentMethod = mapPaymentMethod(raw.cust_params?.[PAYMENT_METHOD_KEY]);
  const transactionId = raw.cust_params?.payment_intent_id ?? `txn_${objectId()}`;

  return {
    id: objectId(),
    person_id: raw.per_id,
    donor_first_name: firstName,
    donor_last_name: lastName,
    amount: Number.parseFloat(raw.donation_amount) || 0,
    credits_earned: raw.growth_credits ?? 0,
    donation_type: raw.donation_type === 'Recurring' ? 'recurring' : 'one_time',
    form_id: raw.form_id ?? '',
    form_name: raw.form_name ?? 'Unknown Form',
    payment_method: paymentMethod,
    transaction_id: transactionId,
    created_at: raw.donation_time,
    isTestEvent,
  };
}

/**
 * Type guard to check if an event is in raw SSE format vs already transformed.
 * Checks for the presence of backend-specific fields.
 *
 * @param event - The event to check
 * @returns True if the event is in raw SSE format
 */
function isRawDonationEvent(event: unknown): event is IRawDonationSSEEvent {
  if (typeof event !== 'object' || event === null) {
    return false;
  }

  const obj = event as Record<string, unknown>;

  // Raw events have donor_name (string), transformed have donor_first_name
  return typeof obj.donor_name === 'string' && typeof obj.donation_amount === 'string';
}

/**
 * Normalizes a donation event to the internal FE format.
 * Handles both raw SSE events and already-transformed events.
 *
 * @param event - Either a raw SSE event or an already-transformed event
 * @returns Event in IDonationCreatedEvent format
 */
function normalizeDonationEvent(
  event: IRawDonationSSEEvent | IDonationCreatedEvent,
): IDonationCreatedEvent {
  if (isRawDonationEvent(event)) {
    return transformRawDonationEvent(event);
  }
  return event;
}

export {
  isRawDonationEvent,
  mapPaymentMethod,
  normalizeDonationEvent,
  splitName,
  transformRawDonationEvent,
};
